import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";

import { db, storage } from "../config/firebase";

const RELATORIO_EMPRESA_DEFAULT = {
  empresaNome: "Ramalho ProFit",
  empresaSubtitulo: "Montagem & Manutenção de Equipamentos Fitness",
  empresaCnpj: "29.252.847/0001-45",
  empresaTelefone: "(11) 95679-8125",
  empresaInstagram: "@ramalhoprofit",
  empresaEmail: "contato@ramalhoprofit.com.br",
  empresaSite: "www.ramalhoprofit.com.br",
  empresaEndereco: "São Paulo - SP",
};

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function moneyBR(value) {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return "0,00";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function dateBR(value) {
  if (!value) return "-";
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString("pt-BR");
}

async function carregarIdentidadeRelatorio(grupoId) {
  const fallback = {
    ...RELATORIO_EMPRESA_DEFAULT,
    logoUrl: "",
  };
  if (!grupoId) return fallback;

  try {
    const snap = await getDoc(doc(db, "grupos", grupoId));
    if (!snap.exists()) return fallback;

    const data = snap.data() || {};
    const rel = data.relatorios || {};

    const empresa = {
      empresaNome: String(rel.empresaNome || data.nome || RELATORIO_EMPRESA_DEFAULT.empresaNome),
      empresaSubtitulo: String(rel.empresaSubtitulo || RELATORIO_EMPRESA_DEFAULT.empresaSubtitulo),
      empresaCnpj: String(rel.empresaCnpj || data.cnpj || RELATORIO_EMPRESA_DEFAULT.empresaCnpj),
      empresaTelefone: String(rel.empresaTelefone || data.telefone || RELATORIO_EMPRESA_DEFAULT.empresaTelefone),
      empresaInstagram: String(rel.empresaInstagram || data.instagram || RELATORIO_EMPRESA_DEFAULT.empresaInstagram),
      empresaEmail: String(rel.empresaEmail || data.email || RELATORIO_EMPRESA_DEFAULT.empresaEmail),
      empresaSite: String(rel.empresaSite || data.site || RELATORIO_EMPRESA_DEFAULT.empresaSite),
      empresaEndereco: String(rel.empresaEndereco || data.endereco || RELATORIO_EMPRESA_DEFAULT.empresaEndereco),
    };

    const logoPath = String(data.logoRelatorioPath || data.logoPath || "").trim();
    let logoUrl = "";
    if (logoPath) {
      try {
        logoUrl = await getDownloadURL(ref(storage, logoPath));
      } catch {
        logoUrl = "";
      }
    }

    return { ...empresa, logoUrl };
  } catch {
    return fallback;
  }
}

function montarCabecalho(identidade, titulo) {
  const logo = identidade?.logoUrl
    ? `<img src="${escapeHtml(identidade.logoUrl)}" style="max-height:82px; max-width:220px; object-fit:contain;">`
    : `<div style="font-size:20px;font-weight:800;color:#0f172a;">${escapeHtml(identidade?.empresaNome || "Empresa")}</div>`;

  return `
    <header style="display:flex;justify-content:space-between;align-items:flex-start;gap:18px;margin-bottom:18px;border-bottom:2px solid #e2e8f0;padding-bottom:12px;">
      <div>${logo}</div>
      <div style="text-align:right;">
        <div style="font-size:20px;font-weight:800;color:#0f172a;">${escapeHtml(titulo)}</div>
        <div style="font-size:11px;color:#475569;margin-top:4px;">Emitido em ${escapeHtml(dateBR(new Date()))}</div>
      </div>
    </header>
    <section style="font-size:11px;color:#334155;margin-bottom:14px;line-height:1.5;">
      <div><strong>${escapeHtml(identidade?.empresaNome || "")}</strong></div>
      <div>${escapeHtml(identidade?.empresaSubtitulo || "")}</div>
      <div>CNPJ: ${escapeHtml(identidade?.empresaCnpj || "-")} • Tel: ${escapeHtml(identidade?.empresaTelefone || "-")}</div>
      <div>Email: ${escapeHtml(identidade?.empresaEmail || "-")} • Site: ${escapeHtml(identidade?.empresaSite || "-")}</div>
      <div>${escapeHtml(identidade?.empresaEndereco || "-")} • ${escapeHtml(identidade?.empresaInstagram || "")}</div>
    </section>
  `;
}

function montarPagina(titulo, identidade, corpo) {
  return `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(titulo)}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; color: #0f172a; margin: 0; padding: 0; }
    .page { padding: 18mm 14mm; }
    h3 { margin: 14px 0 8px; font-size: 14px; }
    .grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
    .card { border:1px solid #cbd5e1; border-radius:8px; padding:8px; font-size:12px; }
    .label { font-size:10px; color:#64748b; text-transform:uppercase; letter-spacing:0.03em; }
    .value { font-size:12px; margin-top:2px; word-break:break-word; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th, td { border:1px solid #cbd5e1; padding:6px 8px; text-align:left; }
    th { background:#f1f5f9; }
    .right { text-align:right; }
    .muted { color:#64748b; }
  </style>
</head>
<body>
  <main class="page">
    ${montarCabecalho(identidade, titulo)}
    ${corpo}
  </main>
</body>
</html>`;
}

export async function gerarPdfInstalacao({ grupoId, dados, retornarBlob = false }) {
  const identidade = await carregarIdentidadeRelatorio(grupoId);

  const linhasEquip = Object.entries(dados?.equipamentosPorTipo || {})
    .filter(([, qtd]) => Number(qtd || 0) > 0)
    .map(([tipo, qtd]) => `<tr><td>${escapeHtml(tipo)}</td><td class="right">${Number(qtd || 0)}</td></tr>`)
    .join("");

  const corpo = `
    <h3>Dados da proposta</h3>
    <div class="grid">
      <div class="card"><div class="label">Cliente</div><div class="value">${escapeHtml(dados?.clienteNome || "-")}</div></div>
      <div class="card"><div class="label">Academia</div><div class="value">${escapeHtml(dados?.academiaNome || "-")}</div></div>
      <div class="card"><div class="label">Status</div><div class="value">${escapeHtml(dados?.status || "-")}</div></div>
      <div class="card"><div class="label">Previsão de dias</div><div class="value">${escapeHtml(String(dados?.previsaoDias ?? "-"))}</div></div>
      <div class="card" style="grid-column:1 / -1;"><div class="label">Observações</div><div class="value">${escapeHtml(dados?.observacoes || "-")}</div></div>
    </div>

    <h3>Equipamentos por tipo</h3>
    <table>
      <thead><tr><th>Tipo</th><th class="right">Quantidade</th></tr></thead>
      <tbody>${linhasEquip || `<tr><td colspan="2" class="muted">Nenhum equipamento informado.</td></tr>`}</tbody>
    </table>

    <h3>Resumo financeiro</h3>
    <table>
      <tbody>
        <tr><td>Valor equipamentos</td><td class="right">R$ ${moneyBR(dados?.valorEquipamentos)}</td></tr>
        <tr><td>Deslocamento</td><td class="right">R$ ${moneyBR(dados?.valorDeslocamento)}</td></tr>
        <tr><td>Pedágio</td><td class="right">R$ ${moneyBR(dados?.valorPedagio)}</td></tr>
        <tr><td>Gastos almoço</td><td class="right">R$ ${moneyBR(dados?.gastosAlmoco)}</td></tr>
        <tr><td>Desconto</td><td class="right">- R$ ${moneyBR(dados?.valorDesconto)}</td></tr>
        <tr><th>Total</th><th class="right">R$ ${moneyBR(dados?.valorTotal)}</th></tr>
      </tbody>
    </table>
  `;

  const html = montarPagina("Proposta de Instalação", identidade, corpo);
  const nome = `Proposta_Instalacao_${String(dados?.clienteNome || "cliente").replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}.pdf`;
  const blob = await gerarPdfDoHtml({ html, fileName: nome, retornarBlob });
  if (retornarBlob) return { blob, fileName: nome };
}

export async function gerarPdfPropostaOs({ grupoId, dados, retornarBlob = false }) {
  const identidade = await carregarIdentidadeRelatorio(grupoId);

  const linhasOs = (dados?.osItens || [])
    .map((os) => `<tr><td>${escapeHtml(os?.osCodigo ? `#${os.osCodigo}` : "-")}</td><td>${escapeHtml(os?.osTitulo || os?.osTipoServico || os?.osId || "-")}</td></tr>`)
    .join("");

  const corpo = `
    <h3>Dados da proposta</h3>
    <div class="grid">
      <div class="card"><div class="label">Cliente</div><div class="value">${escapeHtml(dados?.clienteNome || "-")}</div></div>
      <div class="card"><div class="label">Academia</div><div class="value">${escapeHtml(dados?.academiaNome || "-")}</div></div>
      <div class="card"><div class="label">Status</div><div class="value">${escapeHtml(dados?.status || "-")}</div></div>
      <div class="card"><div class="label">Forma de pagamento</div><div class="value">${escapeHtml(dados?.formaPagamento || "-")}</div></div>
      <div class="card"><div class="label">Data pagamento</div><div class="value">${escapeHtml(dados?.dataPagamento ? dateBR(dados.dataPagamento) : "-")}</div></div>
      <div class="card"><div class="label">Km rodados</div><div class="value">${escapeHtml(String(dados?.kmRodados ?? "0"))} km</div></div>
      <div class="card" style="grid-column:1 / -1;"><div class="label">Observações</div><div class="value">${escapeHtml(dados?.observacoes || "-")}</div></div>
    </div>

    <h3>OS vinculadas</h3>
    <table>
      <thead><tr><th>Código</th><th>Descrição</th></tr></thead>
      <tbody>${linhasOs || `<tr><td colspan="2" class="muted">Nenhuma OS vinculada.</td></tr>`}</tbody>
    </table>

    <h3>Resumo financeiro</h3>
    <table>
      <tbody>
        <tr><td>Mão de obra</td><td class="right">R$ ${moneyBR(dados?.valorMaoObra)}</td></tr>
        <tr><td>Peças</td><td class="right">R$ ${moneyBR(dados?.valorPecas)}</td></tr>
        <tr><td>Deslocamento</td><td class="right">R$ ${moneyBR(dados?.valorDeslocamento)}</td></tr>
        <tr><td>Pedágio</td><td class="right">R$ ${moneyBR(dados?.valorPedagio)}</td></tr>
        <tr><td>Gastos almoço</td><td class="right">R$ ${moneyBR(dados?.gastosAlmoco)}</td></tr>
        <tr><td>Passagem</td><td class="right">R$ ${moneyBR(dados?.valorPassagem)}</td></tr>
        <tr><td>Aluguel carro</td><td class="right">R$ ${moneyBR(dados?.valorAluguelCarro)}</td></tr>
        <tr><td>Desconto</td><td class="right">- R$ ${moneyBR(dados?.valorDesconto)}</td></tr>
        <tr><th>Total</th><th class="right">R$ ${moneyBR(dados?.valorTotal)}</th></tr>
      </tbody>
    </table>
  `;

  const html = montarPagina("Proposta de Ordem de Serviço", identidade, corpo);
  const nome = `Proposta_OS_${String(dados?.clienteNome || "cliente").replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}.pdf`;
  const blob = await gerarPdfDoHtml({ html, fileName: nome, retornarBlob });
  if (retornarBlob) return { blob, fileName: nome };
}

export async function gerarPdfContrato({ grupoId, dados, retornarBlob = false }) {
  const identidade = await carregarIdentidadeRelatorio(grupoId);

  const academias = Array.isArray(dados?.academias) ? dados.academias : [];
  const linhasAcademias = academias
    .map((a) => `<tr><td>${escapeHtml(a?.nome || a?.codigo || a?.id || "-")}</td></tr>`)
    .join("");

  const corpo = `
    <h3>Dados do contrato</h3>
    <div class="grid">
      <div class="card"><div class="label">Cliente</div><div class="value">${escapeHtml(dados?.clienteNome || "-")}</div></div>
      <div class="card"><div class="label">Tipo</div><div class="value">${escapeHtml(dados?.contratoTipo || "-")}</div></div>
      <div class="card"><div class="label">Status</div><div class="value">${escapeHtml(dados?.status || "-")}</div></div>
      <div class="card"><div class="label">Duração</div><div class="value">${escapeHtml(String(dados?.duracaoMeses || "-"))} meses</div></div>
      <div class="card"><div class="label">Data inicial</div><div class="value">${escapeHtml(dados?.dataInicial ? dateBR(dados.dataInicial) : "-")}</div></div>
      <div class="card"><div class="label">Visitas emergenciais</div><div class="value">${escapeHtml(String(dados?.visitasEmergenciais || "0"))}</div></div>
    </div>

    <h3>Academias cobertas</h3>
    <table>
      <thead><tr><th>Academia</th></tr></thead>
      <tbody>${linhasAcademias || `<tr><td class="muted">Nenhuma academia selecionada.</td></tr>`}</tbody>
    </table>
  `;

  const html = montarPagina("Proposta de Contrato", identidade, corpo);
  const nome = `Proposta_Contrato_${String(dados?.clienteNome || "cliente").replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}.pdf`;
  const blob = await gerarPdfDoHtml({ html, fileName: nome, retornarBlob });
  if (retornarBlob) return { blob, fileName: nome };
}

export async function gerarPdfDoHtml({ html, fileName, retornarBlob = false }) {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.width = "794px";
  host.innerHTML = html;
  document.body.appendChild(host);

  try {
    const { default: html2pdf } = await import("html2pdf.js");
    const worker = html2pdf()
      .from(host)
      .set({
        margin: [8, 8, 8, 8],
        filename: fileName,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      });

    if (retornarBlob) {
      return await worker.outputPdf("blob");
    }

    await worker.save();
  } finally {
    document.body.removeChild(host);
  }
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const idx = result.indexOf(",");
      resolve(idx > -1 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Falha ao converter blob para base64"));
    reader.readAsDataURL(blob);
  });
}
